import { z } from "zod";

import { recipeTypeSchema } from "./recipe-domain";

export const targetMetricSchema = z.enum([
  "servingTemperatureC",
  "relativeSweetness",
  "totalSolidsPct",
  "fatPct",
  "saturatedFatRatio",
  "milkFatPct",
  "msnfPct",
  "sugarsPct",
  "stabilizersPerWaterPct",
  "stabilizersPct",
  "emulsifiersPct",
  "alcoholPct",
]);
export const TARGET_METRIC_KEYS = targetMetricSchema.options;
export type TargetMetric = z.infer<typeof targetMetricSchema>;

// Stored values use fractions and Celsius. Display conversion belongs to the UI.
export const TARGET_METRIC_BOUNDS = {
  servingTemperatureC: {},
  relativeSweetness: { min: 0 },
  totalSolidsPct: { min: 0, max: 1 },
  fatPct: { min: 0, max: 1 },
  saturatedFatRatio: { min: 0, max: 1 },
  milkFatPct: { min: 0, max: 1 },
  msnfPct: { min: 0, max: 1 },
  sugarsPct: { min: 0, max: 1 },
  stabilizersPerWaterPct: { min: 0, max: 1 },
  stabilizersPct: { min: 0, max: 1 },
  emulsifiersPct: { min: 0, max: 1 },
  alcoholPct: { min: 0, max: 1 },
} satisfies Record<TargetMetric, { min?: number; max?: number }>;

function orderedRangeSchema(value: z.ZodNumber) {
  return z
    .object({ min: value, max: value })
    .refine((range) => range.min <= range.max, {
      message: "Target minimum must be less than or equal to maximum",
      path: ["min"],
    });
}

export const targetOrderedRangeSchema = orderedRangeSchema(z.number().finite());

export function targetMetricRangeSchema(metric: TargetMetric) {
  const bounds: { min?: number; max?: number } = TARGET_METRIC_BOUNDS[metric];
  let value = z.number().finite();
  if (bounds.min !== undefined) value = value.min(bounds.min);
  if (bounds.max !== undefined) value = value.max(bounds.max);
  return orderedRangeSchema(value);
}

// SAFETY: Every metric is mapped exactly once to its optional range schema.
const configFields = Object.fromEntries(
  TARGET_METRIC_KEYS.map((metric) => [
    metric,
    targetMetricRangeSchema(metric).optional(),
  ]),
) as Record<
  TargetMetric,
  z.ZodOptional<ReturnType<typeof targetMetricRangeSchema>>
>;

export const targetProfileConfigWriteSchema = z.object(configFields).strict();
export const targetProfileNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(50, "Name is too long");
export const targetProfileDescriptionSchema = z.string().max(1000);
export const targetProfileLinkSchema = z.string().url().max(2048);
export const targetProfileRecipeTypesSchema = z
  .array(recipeTypeSchema)
  .min(1, "Select at least one recipe type");

export const targetProfileDomainSchema = z.object({
  name: targetProfileNameSchema,
  description: targetProfileDescriptionSchema.optional(),
  link: targetProfileLinkSchema.optional(),
  recipeTypes: targetProfileRecipeTypesSchema,
  config: targetProfileConfigWriteSchema.default({}),
});
